/**
 * Utility functions for handling locale-aware number parsing
 * Handles both comma (,) and dot (.) as decimal separators
 */

/**
 * Determines the decimal separator based on the current locale
 * @param locale - The current locale (e.g., 'nl', 'en', 'de')
 * @returns The decimal separator for the locale (',' or '.')
 */
export const getDecimalSeparator = (locale: string): ',' | '.' => {
  // Locales that use comma as decimal separator
  const commaLocales = ['nl', 'de', 'fr', 'es', 'it'];
  return commaLocales.includes(locale) ? ',' : '.';
};

/**
 * Parses a number string that may use either comma or dot as decimal separator
 * @param value - The string value to parse
 * @returns The parsed number value or empty string if invalid
 */
export const parseLocaleNumber = (
  value: string | number | undefined,
): number | string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  // If already a number, return it
  if (typeof value === 'number') {
    return value;
  }

  // Convert to string and clean
  const stringValue = String(value);

  // Remove any thousands separators (both . and ,)
  // Handle both formats: 1.234,56 (EU) and 1,234.56 (US)
  let cleaned = stringValue;

  // Detect format by finding the last occurrence of . or ,
  const lastDotIndex = cleaned.lastIndexOf('.');
  const lastCommaIndex = cleaned.lastIndexOf(',');

  if (lastDotIndex > -1 && lastCommaIndex > -1) {
    // Both present - the last one is the decimal separator
    if (lastDotIndex > lastCommaIndex) {
      // Format: 1,234.56 (US format)
      cleaned = cleaned.replace(/,/g, '');
    } else {
      // Format: 1.234,56 (EU format)
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
  } else if (lastCommaIndex > -1) {
    // Only comma - could be thousands or decimal
    // If there are digits after comma, it's decimal
    const afterComma = cleaned.substring(lastCommaIndex + 1);
    if (afterComma.length <= 2 && afterComma.length > 0) {
      // Likely decimal: 50,5 or 50,50
      cleaned = cleaned.replace(',', '.');
    } else {
      // Likely thousands: 1,234
      cleaned = cleaned.replace(/,/g, '');
    }
  }
  // If only dot, leave as is (could be thousands or decimal, parseFloat will handle)

  // Validate that cleaned string contains only digits, optional minus, and optional single dot
  if (!/^-?\d+\.?\d*$/.test(cleaned)) {
    return '';
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? '' : parsed;
};
